/**
 * Admin-frontend feature configuration (docs/358-ADMIN-FEATURE-CONFIGURATION.md v0) —
 * a pure, coverage-gated resolver deciding which nav items/pages/actions an admin
 * deployment (the Mantis admin, `apps/admin-web-mantis`, docs/337) RENDERS. This is a
 * DIFFERENT question from {@link ../admin/adminCapabilities.resolveAdminCapabilities}
 * (docs/332 — "does the backend IMPLEMENT it," version negotiation) and from docs/344's
 * still-unbuilt `resolveTenantFeatures` (server tier — "is this tenant LICENSED for it").
 * All three read the same `AdminFeatureId`/capability vocabulary; the visible/usable set
 * is the intersection (docs/358 §1's table):
 *
 *   config-shown (THIS file) ∩ backend-implemented (docs/332) ∩ tenant-entitled (docs/344)
 *
 * **This is UX, not a security boundary** (docs/358 §5) — hiding a nav item/button here
 * only controls what RENDERS. Any feature backed by a mutation or sensitive-data endpoint
 * MUST also be enforced server-side (docs/344's `requireCapability`, not built yet); a
 * hidden panel is trivially recoverable from the network tab until that lands. The
 * `enforcement` field on each {@link AdminFeatureDescriptor} records which ones need it.
 *
 * The owner's own deployment ships `{ mode: "all" }` (docs/358 §6 — every id on, no
 * config needed at all); a provider/tenant build supplies an `"allow"`-mode (or
 * `"all"`-minus-`disabled`) config that can only ever SUBTRACT from that — never grant a
 * feature the served `capabilities` list (docs/344, when it exists) doesn't also grant.
 */

/**
 * The nav/page + data-visibility grain (docs/358 §3) — matches the owner's "the nav item
 * / page is hidden, not greyed" framing. Kept intentionally coarse (page-level, plus a
 * handful of action/data-visibility ids for credentials/moderation/history/cost) rather
 * than one id per button (docs/358 §10 Q2).
 */
export type AdminFeatureId =
  | "overview"
  | "people"
  | "users.credentials"
  | "users.history"
  | "moderation"
  | "invites"
  | "invite-attempts"
  | "catalogs"
  | "manifests"
  | "files"
  | "feedback"
  | "notices"
  | "progress"
  | "devices"
  | "provider-settings"
  | "usage"
  | "audit"
  | "cost.viewOwnUsage"
  | "cost.viewCostBasis"
  | "analytics"
  | "utilities"
  | "app-links"
  | "ai"
  | "alerts"
  | "monitoring";

export interface AdminFeatureDescriptor {
  id: AdminFeatureId;
  /**
   * The docs/344 `TenantCapabilityId`(s) this feature will be gated by once the server
   * tier ships — INFORMATIONAL only in v0 (docs/344 is still a plan; `packages/core/
   * tenant/planTier.ts` doesn't exist yet, so these are documented capability names, not
   * a type-checked reference). Empty = no server-side gate is planned for this id (a
   * pure convenience surface, e.g. `overview`) — such an id is never subtracted by a
   * served `capabilities` list (there is nothing for the server to withhold).
   */
  capabilities: readonly string[];
  /**
   * Whether a hidden UI is itself a sufficient boundary (docs/358 §5). `"server"` means
   * this feature exposes user data, credentials, moderation actions, cost/margin, or a
   * write endpoint — it MUST also be enforced server-side (docs/344's
   * `requireCapability` + field omission) once that lands; hiding it here is UX only,
   * not a substitute. `"ui"` marks a feature with nothing sensitive behind it.
   */
  enforcement: "ui" | "server";
  /** The owner's own deployment default (docs/358 §6) — always `true`; the owner runs
   * `{ mode: "all" }` and its own full-tier backend, so nothing here is ever hidden. */
  ownerDefault: boolean;
  /**
   * A TYPICAL provider/tenant deployment's default (docs/358 §3's "Typical provider"
   * column) — used only to build {@link SAMPLE_PROVIDER_FEATURE_CONFIG}, a reference/test
   * fixture. Never consulted by {@link resolveAdminFeatures} itself; a real provider's
   * actual config is whatever their `deploy-tenant.sh` (docs/344 §3) injects.
   */
  providerDefault: boolean;
}

/**
 * The single source both the config resolver and (via `apps/admin-web-mantis/src/layout/
 * navItems.ts`'s `feature` field) the nav/route descriptors read — docs/358 §4.5's "single
 * source ... lives in `@equipped/core` ... imported by both admin frontends."
 */
export const ADMIN_FEATURE_REGISTRY: readonly AdminFeatureDescriptor[] = [
  { id: "overview", capabilities: [], enforcement: "ui", ownerDefault: true, providerDefault: true },
  { id: "people", capabilities: ["users.list"], enforcement: "server", ownerDefault: true, providerDefault: true },
  {
    id: "users.credentials",
    capabilities: ["users.setPassword"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  {
    id: "users.history",
    capabilities: ["users.viewHistory"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  {
    id: "moderation",
    capabilities: ["moderation.act"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  { id: "invites", capabilities: ["invites.create"], enforcement: "server", ownerDefault: true, providerDefault: true },
  {
    id: "invite-attempts",
    capabilities: ["invites.viewAttempts"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  { id: "catalogs", capabilities: ["grants.manage"], enforcement: "server", ownerDefault: true, providerDefault: true },
  {
    id: "manifests",
    capabilities: ["manifests.build"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  { id: "files", capabilities: ["files.manage"], enforcement: "server", ownerDefault: true, providerDefault: true },
  {
    id: "feedback",
    capabilities: ["feedback.view"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  { id: "notices", capabilities: ["notices.send"], enforcement: "server", ownerDefault: true, providerDefault: false },
  { id: "progress", capabilities: ["usage.view"], enforcement: "server", ownerDefault: true, providerDefault: false },
  {
    id: "devices",
    capabilities: ["devices.view"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  {
    id: "provider-settings",
    capabilities: ["providerConfig.edit"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: true,
  },
  { id: "usage", capabilities: ["usage.view"], enforcement: "server", ownerDefault: true, providerDefault: false },
  { id: "audit", capabilities: ["audit.view"], enforcement: "server", ownerDefault: true, providerDefault: false },
  {
    id: "cost.viewOwnUsage",
    capabilities: ["cost.viewOwnUsage"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: true,
  },
  {
    id: "cost.viewCostBasis",
    capabilities: ["cost.viewCostBasis"],
    enforcement: "server",
    ownerDefault: true,
    // Explicitly owner-only per docs/358 §3/§6 — never a provider default. A provider's
    // served `capabilities` list (docs/344, when it exists) is not expected to ever
    // include `cost.viewCostBasis`, so this id resolves off even under `{ mode: "all" }`
    // the moment a served list is supplied for a non-owner deployment.
    providerDefault: false,
  },
  { id: "analytics", capabilities: ["usage.view"], enforcement: "server", ownerDefault: true, providerDefault: false },
  {
    id: "utilities",
    capabilities: ["utilities.use"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  {
    id: "app-links",
    capabilities: ["app-links.use"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  { id: "ai", capabilities: ["ai.use"], enforcement: "server", ownerDefault: true, providerDefault: false },
  {
    id: "alerts",
    capabilities: ["alerts.manage"],
    enforcement: "server",
    ownerDefault: true,
    providerDefault: false,
  },
  {
    id: "monitoring",
    capabilities: ["alerts.view", "cost.viewOwnUsage"],
    enforcement: "server",
    ownerDefault: true,
    // The monitoring page's cost widget shows this deployment's own Cloudflare usage/
    // cost basis (docs/347 §7.5's owner-vs-provider split, docs/344's still-unbuilt
    // cost.viewCostBasis axis) — off by default for a provider deployment, like every
    // other owner-only surface here.
    providerDefault: false,
  },
];

export const ADMIN_FEATURE_IDS: readonly AdminFeatureId[] = ADMIN_FEATURE_REGISTRY.map((d) => d.id);

const ADMIN_FEATURE_ID_SET: ReadonlySet<AdminFeatureId> = new Set(ADMIN_FEATURE_IDS);

const ADMIN_FEATURE_BY_ID: ReadonlyMap<AdminFeatureId, AdminFeatureDescriptor> = new Map(
  ADMIN_FEATURE_REGISTRY.map((d) => [d.id, d]),
);

/**
 * The docs/358 §2/§10 Q5 "malformed/absent provider config fails CLOSED" minimal safe
 * set — never silently expose a sensitive feature to a misconfigured provider build.
 * Exported so every admin frontend's env-config parser shares one definition instead of
 * each hand-picking its own fallback.
 */
export const ADMIN_FEATURE_FAIL_CLOSED: readonly AdminFeatureId[] = ["people", "invites"];

/**
 * The build-time config an admin deployment ships (docs/358 §2). `"all"` = the owner's
 * own deployment, everything on (the simple default — needs no config at all). `"allow"`
 * = an explicit provider allow-set. `"all"` + `disabled` = the owner subtracting a
 * not-yet-ready tab without a code change (docs/358 §10 Q6) — still every id ON by
 * default, just a documented subtract list.
 */
export type AdminFeatureConfig =
  | { mode: "all" }
  | { mode: "allow"; features: readonly AdminFeatureId[] }
  | { mode: "all"; disabled: readonly AdminFeatureId[] };

/** Unknown ids are silently ignored (forward-compat, docs/108) — a newer config against
 * an older admin bundle (or vice versa) degrades rather than crashes (docs/358 §7). */
function expandConfig(config: AdminFeatureConfig): Set<AdminFeatureId> {
  if (config.mode === "allow") {
    return new Set(config.features.filter((id) => ADMIN_FEATURE_ID_SET.has(id)));
  }
  const disabled = new Set("disabled" in config && config.disabled ? config.disabled : []);
  return new Set(ADMIN_FEATURE_IDS.filter((id) => !disabled.has(id)));
}

/**
 * Resolve which `AdminFeatureId`s this admin deployment renders.
 *
 * `servedCapabilities` — when supplied, from a future `GET /provider` `capabilities`
 * list (docs/344 §4.4, not emitted by any real provider yet) — is the AUTHORITATIVE,
 * SUBTRACT-ONLY override (docs/358 §2/§6): the effective set is `config ∩ served`. A
 * feature with no declared {@link AdminFeatureDescriptor.capabilities} (e.g. `overview`)
 * is never subtracted — there is nothing for the server to withhold. Omitting
 * `servedCapabilities` entirely (today's reality — every real provider) makes the
 * config file authoritative on the frontend, exactly docs/358 §2's stated v0 posture.
 *
 * Never throws — an unrecognized config shape/id degrades rather than crashing (docs/108
 * forward-compat discipline, mirrors `resolveAdminCapabilities`'s own fail-open posture).
 */
export function resolveAdminFeatures(
  config: AdminFeatureConfig,
  servedCapabilities?: readonly string[],
): ReadonlySet<AdminFeatureId> {
  const fromConfig = expandConfig(config);
  if (!servedCapabilities) return fromConfig;
  const served = new Set(servedCapabilities);
  const result = new Set<AdminFeatureId>();
  for (const id of fromConfig) {
    const caps = ADMIN_FEATURE_BY_ID.get(id)?.capabilities ?? [];
    if (caps.length === 0 || caps.some((c) => served.has(c))) result.add(id);
  }
  return result;
}

/**
 * A reference/test fixture (docs/358 §8 "a sample provider config renders exactly the
 * expected subset") built from each descriptor's {@link AdminFeatureDescriptor.providerDefault}
 * — NOT consulted by {@link resolveAdminFeatures}; a real provider's config is whatever
 * its own `deploy-tenant.sh` (docs/344 §3) injects.
 */
export const SAMPLE_PROVIDER_FEATURE_CONFIG: AdminFeatureConfig = {
  mode: "allow",
  features: ADMIN_FEATURE_REGISTRY.filter((d) => d.providerDefault).map((d) => d.id),
};
